import React, {useMemo} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import {PosOrderForm} from "@/data/entities/order";
import {AddToCartProduct} from "@/data/entities/product";
import {removeProduct, setProductCount} from "@/data/entities/cart/utils";
import ProductInCartLine from "@widgets/pos/order/summary/products/ProductInCartLine";
import {EmptyState, Icon} from "peggirkit";
import {ShoppingCartIcon} from "@heroicons/react/20/solid";
import {FormattedMessage} from "react-intl";
import {useExistingOrder} from "@widgets/pos/order/hooks/UseExistingOrder";

/**
 * Number of products per loyalty reward ID.
 */
const rewardCounts = (products: AddToCartProduct[]): Map<number, number> => {
    const counts = new Map<number, number>();
    products.forEach(product => {
        if (product.loyaltyRewardId !== null && product.loyaltyRewardId !== undefined)
            counts.set(product.loyaltyRewardId, (counts.get(product.loyaltyRewardId) ?? 0) + product.count);
    });
    return counts;
};

type Props = {
    isPending: boolean,
};

const ProductsInCart = ({isPending}: Props) => {
    const {getValues, setValue} = useFormContext<PosOrderForm>();
    const products = useWatch<PosOrderForm, "products">({name: "products"});
    const update = (next: AddToCartProduct[]) =>
        setValue("products", next, {shouldDirty: true, shouldValidate: true});

    // in de component, in plaats van de oude changeCount:
    const existingOrder = useExistingOrder();
    // Rewards redeemed when the order was placed may be kept or removed, not added
    const redeemedRewards: Map<number, number> = useMemo(
        () => rewardCounts(existingOrder?.order.products ?? []),
        [existingOrder],
    );
    const maxCount = (index: number): number | undefined => {
        const product: AddToCartProduct = products[index];
        if (product.loyaltyRewardId === null || product.loyaltyRewardId === undefined)
            return undefined;

        const inOtherLines: number = products
            .filter((p, i) => i !== index && p.loyaltyRewardId === product.loyaltyRewardId)
            .reduce((sum, p) => sum + p.count, 0);
        return Math.max(0, (redeemedRewards.get(product.loyaltyRewardId) ?? 0) - inOtherLines);
    };
    const changeCount = (index: number, count: number) => {
        const max: number | undefined = maxCount(index);
        setValue(
            "products",
            setProductCount(getValues("products"), index, max === undefined ? count : Math.min(count, max)),
            {shouldDirty: true, shouldValidate: true},
        );
    };

    return (
        <ul className={"w-full flex-1 min-h-0 overflow-y-auto px-5 py-4 gap-3 flex flex-col bg-gray-50 dark:bg-slate-900"}>
            {
                products.length === 0 &&
                <EmptyState
                    containerClassname={"py-6"}
                    icon={<Icon icon={ShoppingCartIcon}/>}
                    title={<FormattedMessage id={"noProductsYet"}/>}
                    description={<FormattedMessage id={"noProductsYetDescription"}/>}
                />
            }

            {products.map((product: AddToCartProduct, i: number) => (
                <ProductInCartLine
                    key={i}
                    product={product}
                    onChangeCount={(count: number) => changeCount(i, count)}
                    isPending={isPending}
                    onRemove={() => update(removeProduct(getValues("products"), i))}
                />
            ))}
        </ul>
    );
};

export default ProductsInCart;